import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import Pagination from "../../../components/ui/Pagination";

/** Minimal row shape the cart needs for display. */
export interface CartRow {
  index: number;
  experiment_id: string | null;
  il_name: string | null;
  cargo: string | null;
}

interface Props {
  cart: Map<number, CartRow>;
  onRemove: (pk: number) => void;
  onRemoveMany: (pks: number[]) => void;
  onClear: () => void;
  inProductPks: Set<number>;
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  // Hands the final commit step (ReviewModal + createJob) up to the
  // parent tab so the modal can overlay the full Settings shell rather
  // than clipping inside the cart panel.
  onReview: (opts: {
    cartList: CartRow[];
    scheduledAt: string | null;
  }) => void;
}

const CART_PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const matchesCartSearch = (row: CartRow, term: string): boolean => {
  if (!term) return true;
  const needle = term.toLowerCase();
  const haystacks = [
    String(row.index),
    row.experiment_id ?? "",
    row.il_name ?? "",
    row.cargo ?? "",
  ];
  return haystacks.some((h) => h.toLowerCase().includes(needle));
};

/**
 * Right pane: the cart.
 *
 * Layout uses ``h-full + flex-col`` so the card stretches to match the
 * browser panel's grid-row height, and the table area grows to fill
 * whatever space is left after the title + search + controls have been
 * laid out. When the cart is empty the whole grown region shows a
 * centered empty-state message — no awkward short placeholder.
 *
 * Title-row controls ([Schedule] toggle + [Clear all] outlined button)
 * promote the two most common actions out of the footer so they're
 * reachable without scrolling through a long cart.
 */
const MigrationCartPanel: React.FC<Props> = ({
  cart,
  onRemove,
  onRemoveMany,
  onClear,
  inProductPks,
  showConfirm,
  onReview,
}) => {
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [cartSearch, setCartSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);

  // Stable fingerprint so effects don't fire on every Map identity change
  // while membership stays the same.
  const cartSignature = useMemo(
    () => Array.from(cart.keys()).sort((a, b) => a - b).join(","),
    [cart]
  );

  // Cart / filter / size changed → reset to page 1.
  useEffect(() => {
    setPage(1);
  }, [cartSignature, cartSearch, pageSize]);

  const cartList = useMemo(
    () => Array.from(cart.values()).sort((a, b) => a.index - b.index),
    [cart]
  );

  const filteredCartList = useMemo(
    () => cartList.filter((r) => matchesCartSearch(r, cartSearch)),
    [cartList, cartSearch]
  );

  const isFiltering = cartSearch.trim().length > 0;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCartList.length / pageSize)
  );
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredCartList.slice(start, start + pageSize);
  }, [filteredCartList, page, pageSize]);

  const redundantCount = useMemo(
    () => cartList.filter((r) => inProductPks.has(r.index)).length,
    [cartList, inProductPks]
  );

  const openReview = () => {
    if (cart.size === 0) return;

    let scheduledIso: string | null = null;
    if (scheduleEnabled) {
      if (!scheduledAt) {
        setError("Pick a schedule time or turn off Schedule.");
        return;
      }
      if (scheduledAt.getTime() <= Date.now()) {
        setError("Schedule time must be in the future.");
        return;
      }
      scheduledIso = scheduledAt.toISOString();
    }
    setError(null);

    onReview({
      cartList,
      scheduledAt: scheduledIso,
    });
  };

  const handleRemoveFiltered = () => {
    const pks = filteredCartList.map((r) => r.index);
    if (pks.length === 0) return;
    showConfirm({
      title: `Remove ${pks.length} row(s) from cart`,
      message: `Remove all ${pks.length} row(s) matching "${cartSearch.trim()}" from the cart?`,
      confirmLabel: "Remove",
      variant: "danger",
      onConfirm: () => {
        onRemoveMany(pks);
        setCartSearch("");
      },
    });
  };

  const handleClearAll = () => {
    if (cart.size === 0) return;
    showConfirm({
      title: "Clear cart",
      message: `Remove all ${cart.size} row(s) from the cart? This does not affect beta or product data.`,
      confirmLabel: "Clear all",
      variant: "danger",
      onConfirm: () => {
        onClear();
        setCartSearch("");
      },
    });
  };

  return (
    <div className="flex flex-col h-full p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Title row — left: count, right: Schedule toggle + Clear all */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          Cart
          <span className="ml-2 text-xs font-normal text-gray-500">
            {cart.size} row{cart.size === 1 ? "" : "s"}
          </span>
        </h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setScheduleEnabled((v) => !v)}
            className={`px-2 py-1 text-xs rounded border ${
              scheduleEnabled
                ? "text-indigo-800 bg-indigo-100 border-indigo-300"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
            title="Schedule this job to run at a later time"
          >
            {scheduleEnabled ? "⏰ Scheduled" : "⏰ Schedule"}
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            disabled={cart.size === 0}
            className="px-2 py-1 text-xs text-red-700 bg-white border border-red-200 rounded hover:bg-red-50 disabled:opacity-40"
            title="Remove all rows from cart"
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Schedule datetime appears right under the title when the toggle is on.
          react-datepicker gives a proper popup calendar + time column, which
          the native <input type="datetime-local"> OS widgets couldn't match. */}
      {scheduleEnabled && (
        <div className="mt-2">
          <DatePicker
            selected={scheduledAt}
            onChange={(d: Date | null) => setScheduledAt(d)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="yyyy-MM-dd HH:mm"
            minDate={new Date()}
            placeholderText="Pick a future date & time"
            ariaLabelledBy="Scheduled run time"
            className="w-full p-1.5 text-xs text-gray-900 bg-white border border-indigo-200 rounded focus:ring-blue-500 focus:border-blue-500"
            wrapperClassName="w-full"
            popperPlacement="bottom-start"
          />
        </div>
      )}

      {/* Search — always visible (disabled when empty so it doubles as a state hint) */}
      <div className="flex gap-2 mt-2 mb-2">
        <input
          type="search"
          value={cartSearch}
          onChange={(e) => setCartSearch(e.target.value)}
          placeholder="Filter cart (index / exp_id / name / cargo)"
          disabled={cart.size === 0}
          className="flex-1 p-1.5 text-xs text-gray-900 bg-white border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
        />
        {isFiltering && (
          <button
            type="button"
            onClick={handleRemoveFiltered}
            disabled={filteredCartList.length === 0}
            className="px-2 py-1 text-xs text-red-700 bg-white border border-red-200 rounded hover:bg-red-50 disabled:opacity-40"
            title="Remove all cart rows matching the filter"
          >
            Remove {filteredCartList.length}
          </button>
        )}
      </div>

      {/* Table region — flex-1 stretches to fill whatever vertical space
          the grid row provides. min-h-0 lets it shrink properly so the
          inner scroll area works. */}
      <div className="flex-1 min-h-0 overflow-y-auto border border-gray-100 rounded">
        {cartList.length === 0 ? (
          <div className="flex items-center justify-center h-full text-xs text-gray-400">
            Cart is empty. Add rows from the left.
          </div>
        ) : filteredCartList.length === 0 ? (
          <div className="flex items-center justify-center h-full text-xs text-gray-400">
            No cart rows match "{cartSearch}".
          </div>
        ) : (
          <table className="w-full text-xs text-left">
            <thead className="sticky top-0 text-[10px] text-gray-500 uppercase bg-gray-50">
              <tr>
                <th className="px-2 py-1.5">#</th>
                <th className="px-2 py-1.5">il_name</th>
                <th className="px-2 py-1.5">cargo</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const inProd = inProductPks.has(r.index);
                return (
                  <tr
                    key={r.index}
                    className={`border-t border-gray-100 ${
                      inProd ? "text-gray-400" : "text-gray-800"
                    }`}
                  >
                    <td className="px-2 py-1 tabular-nums">{r.index}</td>
                    <td className="px-2 py-1">
                      {r.il_name ?? "—"}
                      {inProd && (
                        <span
                          className="inline-flex items-center px-1 py-0 ml-1 text-[10px] font-medium rounded bg-gray-200 text-gray-600"
                          title="Already on product — will be skipped"
                        >
                          prod
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1">{r.cargo ?? "—"}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => onRemove(r.index)}
                        className="text-gray-400 hover:text-red-700"
                        title="Remove from cart"
                        aria-label={`Remove ${r.index}`}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {filteredCartList.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-2 mt-2 border-t border-gray-100">
          {/* Rows count + nav (left) · pageSize (right) — mirrors where the
              eye expects status vs. options. */}
          <div className="flex-1 min-w-[10rem]">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={filteredCartList.length}
              unit="rows"
              onPageChange={setPage}
              showJumpInput={false}
              showFirstLast={false}
            />
          </div>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="p-1 text-xs text-gray-900 bg-white border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            aria-label="Cart page size"
          >
            {CART_PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      )}

      {redundantCount > 0 && cartList.length > 0 && (
        <div className="p-2 mt-3 text-xs text-gray-600 border border-gray-200 rounded bg-gray-50">
          {redundantCount} of {cart.size} already in product — these will be
          skipped on Create.
        </div>
      )}

      {error && (
        <div
          className="p-2 mt-3 text-xs text-red-800 border border-red-200 rounded bg-red-50"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="pt-3 mt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={openReview}
          disabled={cart.size === 0}
          className="w-full px-3 py-2 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {scheduleEnabled ? "Schedule job" : "Create + run"}
        </button>
      </div>
    </div>
  );
};

export default MigrationCartPanel;
